import { useEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";

interface RevealProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  delay?: number;
}

/** A small, dependency-free reveal that enhances an already readable page. */
export function Reveal({ children, delay = 0, className = "", style, ...props }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: "0px 0px -12%" });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`home-reveal ${visible ? "is-visible" : ""} ${className}`.trim()}
      style={{ "--reveal-delay": `${delay}ms`, ...style } as CSSProperties}
      {...props}
    >
      {children}
    </div>
  );
}

interface PointerTiltProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  maxTilt?: number;
}

/** Pointer-only depth cue; touch, keyboard and reduced-motion users get a static card. */
export function PointerTilt({ children, className = "", style, maxTilt = 3, ...props }: PointerTiltProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setEnabled(finePointer.matches && !reduced.matches);
    update();
    finePointer.addEventListener?.("change", update);
    reduced.addEventListener?.("change", update);
    return () => {
      finePointer.removeEventListener?.("change", update);
      reduced.removeEventListener?.("change", update);
    };
  }, []);

  const handleMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!enabled || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    ref.current.style.setProperty("--tilt-x", `${(-y * maxTilt).toFixed(2)}deg`);
    ref.current.style.setProperty("--tilt-y", `${(x * maxTilt).toFixed(2)}deg`);
    ref.current.style.setProperty("--pointer-x", `${(x + 0.5) * 100}%`);
    ref.current.style.setProperty("--pointer-y", `${(y + 0.5) * 100}%`);
  };

  const reset = () => {
    if (!ref.current) return;
    ref.current.style.setProperty("--tilt-x", "0deg");
    ref.current.style.setProperty("--tilt-y", "0deg");
    ref.current.style.setProperty("--pointer-x", "50%");
    ref.current.style.setProperty("--pointer-y", "50%");
  };

  return (
    <div
      ref={ref}
      className={`home-pointer-tilt ${className}`.trim()}
      style={{ "--tilt-x": "0deg", "--tilt-y": "0deg", "--pointer-x": "50%", "--pointer-y": "50%", ...style } as CSSProperties}
      onPointerMove={handleMove}
      onPointerLeave={reset}
      {...props}
    >
      {children}
    </div>
  );
}
